import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  fetchSuggestionDetails,
  clearSuggestionDetails,
  selectData,
  selectIsLoading,
  selectSelectedSuggestion,
  selectSuggestionDetails,
  selectDetailsLoading,
  selectResponseType,
  fetchData,
  selectSuggestionsLoading,
  fetchFacebookSuggestions,
  setSuggestions,
  selectSuggestionsError,
  resetState,
  setChatbotId,
  selectUser,
  selectTilesDetails,
  selectRestrictedDataError,
  setCopiedPost,
  selectOpenAiTokens,
} from "../../../../mainApp/features/DataSlice/dataSlice";
import {
  ChatbotContainer,
  ChatbotMain,
  ChatbotDetailsPanel,
  ChatbotForm,
  ChatbotInput,
  ChatbotButton,
  ChatbotExpandButton,
  ChatbotDetailsHeader,
  ChatbotError,
  ChatbotSuggestions,
  SuggestionsHeader,
  SuggestionsBox,
  SuggestionItem,
  SuggestionTitle,
  SuggestionDescription,
  ChatbotDetails,
  ChatbotMessage,
  AppTitle,
  LoadingElement,
  CopyButton,
  CopyButtonContainer,
  ChatbotMessageDescription, // Add new styled component
} from "../styled";
import LoadingPage from "../../TechnicalTabs/LoadingPage";
import ErrorPage from "../../../../mainApp/features/TechnicalTabs/ErrorPage";
import { auth, db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { handleUnauthorizedAccess } from "../../Utility/handleUnauthorizedAccess";
import { FACEBOOK_SUGGESTIONS_COST, FACEBOOK_POST_COST } from "../../Utility/tokenCosts";

const FacebookChatbot = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const tilesDetails = useSelector(selectTilesDetails);
  const restrictedDataError = useSelector(selectRestrictedDataError);
  const suggestionsLoading = useSelector(selectSuggestionsLoading);
  const suggestionsError = useSelector(selectSuggestionsError);
  const suggestions = useSelector(selectData);
  const selectedSuggestion = useSelector(selectSelectedSuggestion);
  const suggestionDetails = useSelector(selectSuggestionDetails);
  const detailsLoading = useSelector(selectDetailsLoading);
  const responseType = useSelector(selectResponseType);
  const [category, setCategory] = useState("");
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [copiedText, setCopiedText] = useState("");
  const [openAiTokens, setOpenAiTokensState] = useState(0);
  const currentTokens = useSelector(selectOpenAiTokens);

  const validationToastId = 'facebook-bot-validation-toast';

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    dispatch(setChatbotId("facebook"));
    dispatch(clearSuggestionDetails());
    dispatch(resetState());
    dispatch(fetchData());
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          dispatch(fetchData());
          const ref = doc(db, "Users", currentUser.uid);
          const snap = await getDoc(ref);
          if (!snap.exists()) {
            console.warn("Dokument użytkownika nie istnieje.");
          }
          await delay(600);
        } catch (error) {
          console.error("Błąd pobierania danych użytkownika:", error);
          await delay(600);
        }
      } else {
        navigate("/login");
      }
      setInitialLoading(false);
    });
    return () => unsubscribe();
  }, [dispatch, navigate]);

  useEffect(() => {
    if (restrictedDataError) {
      navigate("/unauthorized");
    }
  }, [restrictedDataError, navigate]);

  useEffect(() => {
    if (currentTokens !== openAiTokens) {
      setOpenAiTokensState(currentTokens);
    }
  }, [currentTokens]);

  const handleChange = (e) => setCategory(e.target.value);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!category.trim()) {
      dispatch(setSuggestions("Proszę podać kategorię."));
      return;
    }

    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    if (currentTokens < FACEBOOK_SUGGESTIONS_COST) {
      toast.error("Masz za mało tokenów, aby wygenerować pomysły.", toastOptions);
      return;
    }

    dispatch(fetchFacebookSuggestions());
    dispatch({ type: "FETCH_FACEBOOK_SUGGESTIONS", payload: category });
    dispatch(clearSuggestionDetails());
  };

  const handleSuggestionClick = (index) => {
    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    if (currentTokens < FACEBOOK_POST_COST) {
      toast.error("Masz za mało tokenów, aby zobaczyć szczegóły posta.", toastOptions);
      return;
    }

    if (selectedSuggestion === index) {
      dispatch(clearSuggestionDetails());
    } else {
      dispatch(fetchSuggestionDetails(index));
    }
  };

  const toggleDetailsPanel = () => setIsDetailsExpanded((prev) => !prev);

  const handleCopyAndShare = async (text) => {
    try {
      // Kopiowanie tekstu do schowka
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(""), 2000);

      // Otwieranie nowej karty z linkiem do publikacji na Facebooku
      const encodedText = encodeURIComponent(text);
      const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?quote=${encodedText}`;
      window.open(facebookShareUrl, "_blank");
    } catch (err) {
      console.error("Błąd kopiowania:", err);
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        setCopiedText(text);
        setTimeout(() => setCopiedText(""), 2000);

        // Otwieranie nowej karty z linkiem do publikacji na Facebooku
        const encodedText = encodeURIComponent(text);
        const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?quote=${encodedText}`;
        window.open(facebookShareUrl, "_blank");
      } catch (execErr) {
        console.error("Błąd kopiowania (execCommand):", execErr);
      } finally {
        document.body.removeChild(textarea);
      }
    }
  };

  const handleCopyToImageBot = (text) => {
    if (!text) {
      console.error("No text provided to handleCopyToImageBot");
      return;
    }
    dispatch(setCopiedPost(text));
    setCopiedText(text);
    navigate("/image", { state: { copiedPost: text } });
  };

  if (initialLoading || isLoading === "loading") {
    return <LoadingPage />;
  }

  let returned;

  switch (isLoading) {
    case "success":
      if (!user) {
        returned = <LoadingPage isHighZIndex={true} />;
      } else if (tilesDetails.length > 0 && user) {
        returned = (
          <ChatbotContainer isFacebook={true}>
            <ChatbotMain
              isDetailsExpanded={isDetailsExpanded}
              isFacebook={true}
              isDetailsPanelVisible={true}
            >
              <AppTitle isFacebook={true}>Posty na Facebooka</AppTitle>
              <ChatbotForm onSubmit={handleSubmit} isFacebook={true}>
                <ChatbotInput
                  type="text"
                  value={category}
                  onChange={handleChange}
                  placeholder="Wprowadź kategorię np. salon urody w małym mieście"
                  isFacebook={true}
                />
                <ChatbotButton
                  type="submit"
                  disabled={suggestionsLoading}
                  isFacebook={true}
                  tokenCost={FACEBOOK_SUGGESTIONS_COST}
                >
                  Generuj pomysły
                </ChatbotButton>
              </ChatbotForm>
      {!suggestionsLoading && !suggestionsError && (!suggestions || suggestions.length === 0) && responseType !== "message" ? (
  <ChatbotSuggestions>
    <SuggestionsHeader>Podpowiedzi:</SuggestionsHeader>
    <br/>
     <ChatbotMessageDescription isDescription={true}>
      <span style={{ marginRight: 8, color: "#1e90ff" }}>•</span>Wprowadź temat Twojego fanpage i oczekiwanych pomysłów, np. Agent ubezpieczeniowy, ubezpieczenia OC
    </ChatbotMessageDescription>
  </ChatbotSuggestions>
) : suggestionsLoading ? (
  <LoadingElement isFacebook={true}>Ładowanie</LoadingElement>
) : suggestionsError ? (
  <ChatbotError isFacebook={true}>{suggestionsError}</ChatbotError>
) : suggestions && responseType === "message" ? (
  <ChatbotMessage isSuggestions={true} isFacebook={true}>
    {suggestions}
  </ChatbotMessage>
) : Array.isArray(suggestions) && responseType === "suggestions" ? (
  <ChatbotSuggestions>
    <SuggestionsHeader>Pomysły:</SuggestionsHeader>
    <SuggestionsBox>
      {suggestions.map((suggestion, index) => (
        <SuggestionItem
          key={index}
          onClick={() => handleSuggestionClick(index)}
          tokenCost={FACEBOOK_POST_COST}
        >
          <SuggestionTitle isFacebook={true}>
            {suggestion.title}
          </SuggestionTitle>
          <SuggestionDescription isFacebook={true}>
            {suggestion.description || "Brak opisu dla tej sugestii."}
          </SuggestionDescription>
        </SuggestionItem>
      ))}
    </SuggestionsBox>
  </ChatbotSuggestions>
) : (
  <ChatbotError isFacebook={true}>
    Brak sugestii lub wystąpił błąd.
  </ChatbotError>
)}
            </ChatbotMain>
            <ChatbotDetailsPanel
              isDetailsExpanded={isDetailsExpanded}
              isFacebook={true}
            >
              <ChatbotDetailsHeader
                isDetailsExpanded={isDetailsExpanded}
                isFacebook={true}
              />
              <ChatbotExpandButton
                isDetailsExpanded={isDetailsExpanded}
                onClick={toggleDetailsPanel}
                isFacebook={true}
              >
                {isDetailsExpanded ? "Zwiń szczegóły" : "Rozwiń szczegóły"}
              </ChatbotExpandButton>
              {selectedSuggestion !== null && suggestions.length > 0 ? (
                detailsLoading ? (
                  <LoadingElement>Ładowanie szczegółów</LoadingElement>
                ) : suggestionDetails ? (
                  <>
                    <ChatbotDetails isFacebook={true}>
                      <SuggestionsHeader>
                        {suggestions[selectedSuggestion].title.replace(
                          /\*\*(.*?)\*\*/g,
                          "$1"
                        )}
                      </SuggestionsHeader>
                      {suggestionDetails.split("\n").map((line, i) => {
                        const cleanedLine = line.replace(/\*\*(.*?)\*\*/g, "$1");
                        const subheadingMatch = cleanedLine.match(/^(\d+\.\s)(.+)/);
                        const inspirationMatch = cleanedLine.match(/^-\s(.+)/);

                        if (subheadingMatch) {
                          return (
                            <p key={i} className="subheading">
                              {cleanedLine}
                            </p>
                          );
                        } else if (inspirationMatch) {
                          return (
                            <p key={i} className="inspiration">
                              {cleanedLine}
                            </p>
                          );
                        }
                        return <p key={i}>{cleanedLine}</p>;
                      })}
                    </ChatbotDetails>
                    <CopyButtonContainer>
                      <CopyButton
                        onClick={() => handleCopyAndShare(suggestionDetails)}
                        isFacebook={true}
                      >
                        {copiedText === suggestionDetails
                          ? "Skopiowano!"
                          : "Skopiuj tekst i udostępnij"}
                      </CopyButton>
                      <CopyButton
                        onClick={() => handleCopyToImageBot(suggestionDetails)}
                        disabled={detailsLoading}
                        isFacebook={true}
                      >
                        {copiedText === suggestionDetails
                          ? "Skopiowano do generatora obrazów!"
                          : "Dodaj zdjęcie!"}
                      </CopyButton>
                    </CopyButtonContainer>
                  </>
                ) : (
                  <ChatbotError>
                    Wystąpił błąd podczas ładowania szczegółów.
                  </ChatbotError>
                )
              ) : (
                <ChatbotMessage isFacebook={true}>
                  Wybierz pomysł, aby zobaczyć gotowy post.
                </ChatbotMessage>
              )}
            </ChatbotDetailsPanel>
          </ChatbotContainer>
        );
      } else {
        returned = handleUnauthorizedAccess();
      }
      break;
    case "error":
      returned = <ErrorPage />;
      break;
    default:
      returned = <LoadingPage />;
  }

  return returned;
};

export default FacebookChatbot;